import React, { useState } from 'react';
import { SearchIcon, PlusIcon, XIcon, UserIcon, MailIcon, PhoneIcon, AlertTriangleIcon } from 'lucide-react';
interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  isPrimary: boolean;
  associations: Array<{
    type: string;
    name: string;
    id: string;
  }>;
}
interface ContactSelectorProps {
  selectedContactIds: string[];
  onContactsChange: (contactIds: string[]) => void;
  availableContacts: Contact[];
  associationType: 'Vendor' | 'Entity' | 'Site' | 'Org Unit';
  associationId: string;
  associationName: string;
}
export function ContactSelector({
  selectedContactIds,
  onContactsChange,
  availableContacts,
  associationType,
  associationId,
  associationName
}: ContactSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const selectedContacts = availableContacts.filter(c => selectedContactIds.includes(c.id));
  // Helper function to check if a contact has conflicting associations
  const hasConflictingAssociation = (contact: Contact): boolean => {
    const isVendorAssociation = associationType === 'Vendor';
    const isOrgUnitAssociation = ['Entity', 'Site', 'Org Unit'].includes(associationType);
    // Check if contact has any associations
    const hasVendorAssociation = contact.associations.some(assoc => assoc.type === 'Vendor');
    const hasOrgUnitAssociation = contact.associations.some(assoc => ['Entity', 'Site', 'Org Unit'].includes(assoc.type));
    // Rule: Cannot link a contact to both Vendor and internal Org Unit
    if (isVendorAssociation && hasOrgUnitAssociation) {
      return true;
    }
    if (isOrgUnitAssociation && hasVendorAssociation) {
      return true;
    }
    return false;
  };
  // Get conflicting association details for error message
  const getConflictingAssociations = (contact: Contact): string[] => {
    const isVendorAssociation = associationType === 'Vendor';
    if (isVendorAssociation) {
      return contact.associations.filter(assoc => ['Entity', 'Site', 'Org Unit'].includes(assoc.type)).map(assoc => `${assoc.type}: ${assoc.name}`);
    } else {
      return contact.associations.filter(assoc => assoc.type === 'Vendor').map(assoc => `${assoc.type}: ${assoc.name}`);
    }
  };
  const filteredContacts = availableContacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) || contact.email.toLowerCase().includes(searchTerm.toLowerCase()) || contact.role && contact.role.toLowerCase().includes(searchTerm.toLowerCase());
    const notAlreadySelected = !selectedContactIds.includes(contact.id);
    return matchesSearch && notAlreadySelected;
  });
  const handleAddContact = (contactId: string) => {
    const contact = availableContacts.find(c => c.id === contactId);
    if (!contact) return;
    // Validate for conflicting associations
    if (hasConflictingAssociation(contact)) {
      const conflicts = getConflictingAssociations(contact);
      const conflictType = associationType === 'Vendor' ? 'internal organization units' : 'vendors';
      setValidationError(`Cannot add ${contact.name}. This contact is already associated with ${conflictType}: ${conflicts.join(', ')}. A contact cannot be linked to both vendors and internal organization units.`);
      return;
    }
    setValidationError(null);
    onContactsChange([...selectedContactIds, contactId]);
    setSearchTerm('');
    setShowDropdown(false);
  };
  const handleRemoveContact = (contactId: string) => {
    setValidationError(null);
    onContactsChange(selectedContactIds.filter(id => id !== contactId));
  };
  return <div className="space-y-4" data-id="element-9350">
      {/* Header with Add Button */}
      <div className="flex justify-between items-start" data-id="element-9351">
        <div data-id="element-9352">
          <h4 className="text-sm font-medium text-gray-700" data-id="element-9353">
            {selectedContacts.length > 0 ? 'Associated Contacts' : 'Add Contact'}
          </h4>
          {selectedContacts.length === 0 && <p className="text-sm text-gray-500 mt-1" data-id="element-9354">
              No contacts associated yet
            </p>}
        </div>
        <button type="button" onClick={() => {
        // In a real app, this would open a contact form modal
        // For now, we'll show a message
        alert('Contact form would open here. This feature requires a ContactForm component to be created.');
      }} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9355">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-9356" />
          New
        </button>
      </div>

      {/* Validation Error */}
      {validationError && <div className="rounded-md bg-red-50 p-4 border border-red-200" data-id="element-9357">
          <div className="flex" data-id="element-9358">
            <div className="flex-shrink-0" data-id="element-9359">
              <AlertTriangleIcon className="h-5 w-5 text-red-400" data-id="element-9360" />
            </div>
            <div className="ml-3" data-id="element-9361">
              <h3 className="text-sm font-medium text-red-800" data-id="element-9362">
                Association Conflict
              </h3>
              <div className="mt-2 text-sm text-red-700" data-id="element-9363">
                <p data-id="element-9364">{validationError}</p>
              </div>
              <div className="mt-3" data-id="element-9365">
                <button type="button" onClick={() => setValidationError(null)} className="text-sm font-medium text-red-800 hover:text-red-900" data-id="element-9366">
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Selected Contacts */}
      {selectedContacts.length > 0 && <div className="space-y-3" data-id="element-9367">
          {selectedContacts.map(contact => <div key={contact.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-9368">
              <div className="flex items-start space-x-3" data-id="element-9369">
                <div className="flex-shrink-0" data-id="element-9370">
                  <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-9371">
                    <UserIcon className="h-5 w-5 text-indigo-600" data-id="element-9372" />
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-9373">
                  <p className="text-sm font-medium text-gray-900" data-id="element-9374">
                    {contact.name}
                  </p>
                  {contact.role && <p className="text-sm text-gray-500" data-id="element-9375">{contact.role}</p>}
                  <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-9376">
                    <MailIcon className="h-4 w-4 mr-1" data-id="element-9377" />
                    {contact.email}
                  </div>
                  {contact.phone && <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-9378">
                      <PhoneIcon className="h-4 w-4 mr-1" data-id="element-9379" />
                      {contact.phone}
                    </div>}
                </div>
              </div>
              <button type="button" onClick={() => handleRemoveContact(contact.id)} className="ml-4 text-gray-400 hover:text-red-600" data-id="element-9380">
                <XIcon className="h-5 w-5" data-id="element-9381" />
              </button>
            </div>)}
        </div>}
    </div>;
}